import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useDeviceCheck } from "hooks";

export const GSAPAnimation = ({
  onScreenAnimation,
  delay = 0,
  children,
  className = "",
}) => {
  const ref = useRef(null);
  const { isMobile } = useDeviceCheck();

  useEffect(() => {
    if (onScreenAnimation) {
      gsap.registerPlugin(ScrollTrigger);
      switch (onScreenAnimation) {
        case "zoom-in":
          onScreenZoomIn();
          break;
        case "fadeUp":
          onFadeUp(delay);
          break;
        case "easeIn":
          onEaseIn();
          break;
        case "scrollText":
          onScrollText();
          break;
        case "parallax":
          onParallax();
          break;
        default:
          break;
      }
    }
  }, [isMobile]); //eslint-disable-line

  const onScreenZoomIn = () => {
    gsap.fromTo(
      ref.current,
      { scale: 0.85, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 2,
        scrollTrigger: {
          trigger: ref.current,
          start: "top 100%",
          end: "top 80%",
          scrub: 1.5,
        },
      }
    );
  };

  const onFadeUp = (delay = 0) => {
    gsap.fromTo(
      ref.current,
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        ease: "cubic-bezier(0.17, 0.17, 0.3, 1)",
        duration: 1,
        delay,
        scrollTrigger: {
          trigger: ref.current,
          toggleActions: "play none restart reverse",
        },
      }
    );
  };

  const onEaseIn = () => {
    if (isMobile) {
      ref.current.style.opacity = 0;
      setTimeout(() => {
        gsap.fromTo(
          ref.current,
          { y: 100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            ease: "cubic-bezier(0.5, 0.35, 0.15, 1)",
            duration: 0.75,
            scrollTrigger: {
              trigger: ref.current,
              start: "top-=100px bottom",
              end: "bottom bottom",
              toggleActions: "play none none reverse",
            },
            onComplete: () => {
              //do NOT activate this. In IOS not work well
              //ScrollTrigger.refresh();
            },
          }
        );
      }, 1000);
    } else
      gsap.fromTo(
        ref.current,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: "cubic-bezier(0.5, 0.35, 0.15, 1)",
          duration: 0.75,
          scrollTrigger: {
            trigger: ref.current,
            start: "top-=100px bottom",
            end: "bottom bottom",
            toggleActions: "play none restart reverse",
          },
          onComplete: () => {
            ScrollTrigger.refresh();
          },
        }
      );
  };

  const onScrollText = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      children.props.dangerouslySetInnerHTML.__html,
      "text/html"
    );
    const classname = Array.from(doc.body.querySelectorAll("*"))
      .map((element) => element.className)
      .filter(Boolean)
      .join(" ");

    const textElement = ref.current;
    if (textElement) {
      const textContent = textElement.innerText || textElement.textContent;
      const words = textContent.split(/\s+/);
      const wrappedWords = words
        .map((word) => `<span class="word">${word}</span>`)
        .join(" ");
      textElement.innerHTML = wrappedWords;
      const wordsElement = textElement.querySelectorAll(".word");

      gsap.fromTo(
        wordsElement,
        { opacity: 0.2 },
        {
          opacity: 1,
          duration: 0.5,
          stagger: 0.05,
          scrollTrigger: {
            trigger: textElement,
            start: "top bottom",
            end: "25% center",
            scrub: true,
            toggleActions: "restart pause reverse pause",
          },
        }
      );

      gsap.set(textElement, { className: `${classname}` });
    }
  };

  const onParallax = () => {
    const heightCounterMobile =
      document.querySelector(".counter")?.offsetHeight;

    gsap.set(ref.current, { position: "absolute", top: 0 });
    gsap.to(ref.current, {
      scrollTrigger: {
        trigger: ref.current,
        start: "0% 80%",
        end: isMobile ? `+=${heightCounterMobile} top` : `+=321 top`,
        scrub: 0.5,
        toggleActions: "play none reverse none",
      },
      top: isMobile ? `-${15}vh` : `-${70}px`,
      ease: "none",
    });
  };

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};
GSAPAnimation.propTypes = {
  onScreenAnimation: PropTypes.string,
  delay: PropTypes.number,
  children: PropTypes.node,
  className: PropTypes.string,
};
